'use client';
import React, { useState } from 'react';
import { ArrowRight, Download, Printer, Save, RotateCcw, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { saveProject } from '@/lighting-app/data/projectStorage';
import type { DesignWizardState } from './DesignWizardClient';
import type { LightingCalculationResult } from '@/lighting-app/data/lightingStandards';
import RoomLightingPlan from './RoomLightingPlan';

interface StepResultsProps {
  state: DesignWizardState;
  result: LightingCalculationResult;
  onBack: () => void;
  onReset: () => void;
}

export default function StepResults({ state, result, onBack, onReset }: StepResultsProps) {
  const [projectName, setProjectName] = useState(
    state.roomType ? `إنارة ${state.roomType.nameAr}` : 'مشروع إضاءة'
  );
  const [saved, setSaved] = useState(false);

  const statusConfig = {
    good: { icon: CheckCircle, color: 'text-success', bg: 'bg-green-50 border-green-200', label: 'الإضاءة مناسبة', emoji: '🟢' },
    warning: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-yellow-50 border-yellow-200', label: 'الإضاءة تحتاج مراجعة', emoji: '🟡' },
    poor: { icon: XCircle, color: 'text-danger', bg: 'bg-red-50 border-red-200', label: 'الإضاءة غير كافية', emoji: '🔴' },
  };

  const sc = statusConfig[result.status];
  const StatusIcon = sc.icon;

  const handleSave = () => {
    if (!state.roomType) return;
    // Backend integration point: POST /api/projects
    saveProject({
      name: projectName,
      type: 'design',
      roomTypeId: state.roomType.id,
      roomTypeNameAr: state.roomType.nameAr,
      area: result.area,
      targetLux: result.targetLux,
      fixtureCount: result.fixtureCount,
      totalWatts: result.totalWatts,
      estimatedLux: result.estimatedLux,
      status: result.status,
      data: { state, result },
    });
    setSaved(true);
    toast.success(`تم حفظ المشروع: ${projectName}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const metrics = [
    { label: 'الإضاءة المستهدفة', value: `${result.targetLux}`, unit: 'Lux', highlight: false },
    { label: 'مساحة المكان', value: `${result.area.toFixed(1)}`, unit: 'm²', highlight: false },
    { label: 'التدفق الضوئي المطلوب', value: `${Math.round(result.requiredLumens).toLocaleString('ar')}`, unit: 'Lumen', highlight: false },
    { label: 'عدد المصابيح', value: `${result.fixtureCount}`, unit: 'مصباح', highlight: true },
    { label: 'Lumen لكل مصباح', value: `${parseFloat(state.fixtureLumens).toLocaleString('ar')}`, unit: 'Lumen', highlight: false },
    { label: 'القدرة الكلية', value: `${result.totalWatts}`, unit: 'Watt', highlight: false },
    { label: 'الإضاءة المتوقعة', value: `${Math.round(result.estimatedLux)}`, unit: 'Lux', highlight: true },
  ];

  return (
    <div className="slide-up max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-4xl mb-3">💡</div>
        <h2 className="text-3xl font-bold text-foreground mb-2">تصميم الإضاءة جاهز</h2>
        <p className="text-muted-foreground">فيما يلي نتائج حساب الإضاءة لـ {state.roomType?.nameAr}</p>
      </div>

      {/* Status Card */}
      <div className={`rounded-2xl border-2 p-6 mb-8 ${sc.bg} flex items-center gap-4`}>
        <StatusIcon size={40} className={sc.color} />
        <div>
          <div className="text-xl font-bold text-foreground">{sc.emoji} {result.statusAr}</div>
          <div className="text-sm text-muted-foreground mt-1">
            الإضاءة المتوقعة: {Math.round(result.estimatedLux)} Lux | المستهدف: {result.targetLux} Lux
          </div>
        </div>
        <div className="mr-auto text-right">
          <div className="text-3xl font-bold text-primary font-tabular">{result.luxPercentage}%</div>
          <div className="text-xs text-muted-foreground">من الإضاءة المستهدفة</div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {metrics.map((m) => (
          <div
            key={`metric-${m.label}`}
            className={`result-metric-card ${m.highlight ? 'border-primary/30 bg-accent' : ''}`}
          >
            <div className="text-xs text-muted-foreground font-semibold mb-1">{m.label}</div>
            <div className={`text-2xl font-bold font-tabular ${m.highlight ? 'text-primary' : 'text-foreground'}`}>
              {m.value}
            </div>
            <div className="text-xs text-muted-foreground">{m.unit}</div>
          </div>
        ))}
      </div>

      {/* Room Layout Plan */}
      <div className="wizard-card p-6 mb-8">
        <h3 className="text-lg font-bold text-foreground mb-2">مخطط توزيع الإنارة</h3>
        <p className="text-sm text-muted-foreground mb-4">
          التوزيع المقترح: {result.rows} × {result.cols} |
          المسافة بين المصابيح: {result.spacingX.toFixed(1)}م × {result.spacingY.toFixed(1)}م |
          المسافة من الجدران: {result.wallOffsetX.toFixed(1)}م
        </p>
        <RoomLightingPlan
          length={parseFloat(state.length)}
          width={parseFloat(state.width)}
          rows={result.rows}
          cols={result.cols}
          spacingX={result.spacingX}
          spacingY={result.spacingY}
          wallOffsetX={result.wallOffsetX}
          wallOffsetY={result.wallOffsetY}
          fixtureCount={result.fixtureCount}
        />
      </div>

      {/* Save Project */}
      <div className="wizard-card p-6 mb-8">
        <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
          <Save size={18} className="text-primary" />
          حفظ المشروع
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="اسم المشروع"
            className="flex-1 px-4 py-3 rounded-xl border-2 border-border bg-card text-foreground focus:outline-none focus:border-primary"
          />
          <button
            onClick={handleSave}
            disabled={saved}
            className={`px-6 py-3 rounded-xl font-bold transition-all ${
              saved
                ? 'bg-success text-white cursor-not-allowed' :'btn-primary'
            }`}
          >
            {saved ? '✓ محفوظ' : 'حفظ'}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 justify-between">
        <button onClick={onBack} className="btn-secondary px-6 py-3 rounded-xl flex items-center gap-2">
          <ArrowRight size={18} />
          <span>تعديل</span>
        </button>

        <div className="flex gap-3">
          <button
            onClick={handlePrint}
            className="px-5 py-3 rounded-xl border-2 border-border text-secondary font-semibold flex items-center gap-2 hover:border-primary hover:text-primary transition-all"
          >
            <Printer size={18} />
            <span className="hidden sm:inline">طباعة</span>
          </button>
          <button
            onClick={() => toast.info('ميزة تنزيل PDF ستكون متاحة قريباً')}
            className="px-5 py-3 rounded-xl border-2 border-border text-secondary font-semibold flex items-center gap-2 hover:border-primary hover:text-primary transition-all"
          >
            <Download size={18} />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={onReset}
            className="btn-primary px-6 py-3 rounded-xl flex items-center gap-2"
          >
            <RotateCcw size={16} />
            <span>تصميم جديد</span>
          </button>
        </div>
      </div>
    </div>
  );
}